import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import {
  CreditCard,
  Schedule,
  AccountBalanceWallet,
  AccountBalance,
  CheckCircle
} from '@mui/icons-material';
import { mockGateways, getCheapestGateway } from '../../../mockData/paymentGateways';
import MockDataIndicator from '../../Common/MockDataIndicator';

/**
 * PaymentGatewaySelector Component
 * Allows users to select a payment gateway for wallet topup
 * Uses mock data from paymentGateways.js
 *
 * @param {number} amount - The topup amount
 * @param {function} onGatewaySelect - Callback when gateway is selected
 * @param {string} selectedGateway - Currently selected gateway ID
 */
const PaymentGatewaySelector = ({
  amount = 0,
  onGatewaySelect,
  selectedGateway = null
}) => {
  const [gateways, setGateways] = useState([]);
  const [isMockData, setIsMockData] = useState(true);
  const [cheapestGateway, setCheapestGateway] = useState(null);

  // Icon mapping for gateway types
  const getGatewayIcon = (type) => {
    const icons = {
      'CARD': <CreditCard sx={{ fontSize: 32 }} />,
      'BNPL': <Schedule sx={{ fontSize: 32 }} />,
      'WALLET': <AccountBalanceWallet sx={{ fontSize: 32 }} />,
      'BANK': <AccountBalance sx={{ fontSize: 32 }} />
    };
    return icons[type] || <CreditCard sx={{ fontSize: 32 }} />;
  };

  // Calculate fee for a gateway
  const calculateFee = (gateway) => {
    if (!amount || amount <= 0) return 0;
    return (amount * gateway.feePercentage / 100) + gateway.feeFixed;
  };

  // Calculate net amount after fee
  const calculateNetAmount = (gateway) => {
    if (!amount || amount <= 0) return 0;
    return amount - calculateFee(gateway);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(value);
  };

  // NGenius is the only integrated gateway — always shown first and selectable
  const ngeniusGateway = {
    id: 'gateway-ngenius',
    gatewayName: 'NGenius',
    gatewayType: 'CARD',
    displayName: 'Credit/Debit Card via NGenius',
    isActive: true,
    isIntegrated: true,
    feePercentage: 0,
    feeFixed: 0,
    currency: 'AED',
    supportedCurrencies: ['AED', 'USD', 'EUR', 'GBP'],
  };

  useEffect(() => {
    // NGenius is the only integrated gateway; show mock gateways as disabled "Coming Soon"
    const otherGateways = mockGateways.filter(g => g.isActive).map(g => ({
      ...g,
      isIntegrated: false,
    }));
    setGateways([ngeniusGateway, ...otherGateways]);
    setIsMockData(false);

    // NGenius has 0% fee so it's always cheapest
    if (amount && amount > 0) {
      setCheapestGateway({
        gatewayId: ngeniusGateway.id,
        gatewayName: ngeniusGateway.gatewayName,
        fee: 0,
        feePercentage: 0,
        feeFixed: 0,
      });
    }
  }, [amount]);

  const handleGatewayChange = (event) => {
    const gatewayId = event.target.value;
    const gateway = gateways.find(g => g.id === gatewayId);
    if (onGatewaySelect) {
      onGatewaySelect({
        gatewayId: gateway.id,
        gatewayName: gateway.gatewayName,
        gatewayType: gateway.gatewayType,
        fee: calculateFee(gateway),
        netAmount: calculateNetAmount(gateway)
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Select Payment Method
        </Typography>
        <MockDataIndicator isMock={isMockData} position="inline" size="small" />
      </Box>

      {amount > 0 && cheapestGateway && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Recommended:</strong> {cheapestGateway.gatewayName} has the lowest fee
            ({formatCurrency(cheapestGateway.fee)}) for this amount
          </Typography>
        </Alert>
      )}

      <FormControl component="fieldset" fullWidth>
        <RadioGroup value={selectedGateway || ''} onChange={handleGatewayChange}>
          <Grid container spacing={2}>
            {gateways.map((gateway) => {
              const fee = calculateFee(gateway);
              const netAmount = calculateNetAmount(gateway);
              const isSelected = selectedGateway === gateway.id;
              const isCheapest = cheapestGateway?.gatewayId === gateway.id;
              const isDisabled = gateway.isIntegrated === false;

              return (
                <Grid item xs={12} md={6} key={gateway.id}>
                  <Card
                    sx={{
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isDisabled ? 'grey.100' : isSelected ? 'action.selected' : 'background.paper',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.6 : 1,
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': isDisabled ? {} : {
                        borderColor: 'primary.main',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => {
                      if (!isDisabled) {
                        handleGatewayChange({ target: { value: gateway.id } });
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <FormControlLabel
                          value={gateway.id}
                          control={<Radio disabled={isDisabled} />}
                          label=""
                          sx={{ margin: 0 }}
                        />
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {getGatewayIcon(gateway.gatewayType)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {gateway.displayName}
                            </Typography>
                            {isDisabled && (
                              <Chip
                                label="Coming Soon"
                                size="small"
                                color="default"
                                sx={{ height: 20 }}
                              />
                            )}
                            {!isDisabled && isCheapest && amount > 0 && (
                              <Chip
                                label="Best Value"
                                size="small"
                                color="success"
                                icon={<CheckCircle />}
                                sx={{ height: 20 }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {gateway.gatewayName}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Fee:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {gateway.feePercentage}% + {formatCurrency(gateway.feeFixed)}
                        </Typography>
                      </Box>

                      {amount > 0 && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Processing Fee:
                            </Typography>
                            <Typography variant="body2" color="error.main" fontWeight={500}>
                              - {formatCurrency(fee)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Amount to Pay:
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {formatCurrency(amount)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={600}>
                              You'll Receive:
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {formatCurrency(netAmount)}
                            </Typography>
                          </Box>
                        </>
                      )}

                      <Box sx={{ mt: 1.5 }}>
                        <Chip
                          label={`Supports: ${gateway.supportedCurrencies.join(', ')}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </RadioGroup>
      </FormControl>

      {gateways.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body1" color="text.secondary">
            No payment gateways are currently available.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Please contact support or try again later.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PaymentGatewaySelector;
